import {
	type Commit,
	collabKey,
	getVersion,
	receiveCommitTransaction,
	sendableCommit,
} from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { type Getter, type Setter, atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily } from "jotai/utils";
import type { Node } from "prosemirror-model";
import type { EditorState, Transaction } from "prosemirror-state";
import { type Step } from "prosemirror-transform";

import { safeStringify } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { type MdPageCollabConnection } from "../reboot/collab.types";
import type { EditorStateParams } from "./editor-state.types";
import {
	PmCollabSubscriptionFailureError,
	type PmDiskLoadError,
	PmEditorStateNotLoadedError,
	PmMisconfiguredError,
	PmPersistError,
	type PmRebootLoadError,
	type PmRemoteLoadError,
	PmTransactionProcHaltedError,
} from "./errors";
import {
	$cleanupPresence,
	$onPresenceIndicatorsReceived,
	$onRemoteCommitsReceived,
} from "./presence.atom";
import { type CreateEditorStateInput, createEditorState } from "./util";

const logger = new Logger("editor-state");

interface EditorStateContainer {
	state: EditorState | null;
	params: EditorStateParams;
	error:
		| PmEditorStateNotLoadedError
		| PmPersistError
		| PmCollabSubscriptionFailureError
		| PmRebootLoadError
		| PmDiskLoadError
		| PmRemoteLoadError
		| null;
	/** Optional collab connection, only set for Reboot-loaded states */
	connection?: MdPageCollabConnection;
	/** AbortController for stopping collab listeners */
	abortController?: AbortController;
	/**
	 * The document state at the time of the last successful disk persist.
	 * Used for 3-way merge when applying external file changes.
	 */
	lastPersistedDoc?: Node;
	/**
	 * Steps applied locally since the last successful disk persist.
	 * Used to compute a mapping for rebasing external changes onto local edits.
	 */
	foreignSteps?: Step[];
	docChanged: boolean;
}

/**
 * INTERNAL atom holding the EditorState value and params. Use $editorState for reading/writing.
 */
const $editorStateValue = atomFamily((key: string) => {
	// IMPORTANT: MUST CAST here to work around a typing bug.
	//
	// With `strictNullChecks: false`, `null` is assignable to function types, so `atom(null)`
	// matches jotai's `atom(read: Function): Atom<T>` (read-only) overload. Casting `null` to
	// `EditorStateContainer | null` seems to resolve the issue.
	//
	// NOTE: `atom<EditorStateContainer | null>` DOES NOT fix the situation. You MUST cast.
	const valueAtom = atom(null as EditorStateContainer | null);
	valueAtom.debugLabel = `editorStateValue::${key}`;
	return valueAtom;
});

/**
 * Provides read/write access to an `EditorState` instance with optional collab and persistence.
 */
export const $editorState = atomFamily((key: string) => {
	const $stateAtom = atom(
		(get): EditorState | null => get($editorStateValue(key))?.state ?? null,

		(get, set, transaction: Transaction) => {
			////////////////////////////////////////////////////////////////////////////////////////
			// IMPORTANT: SYNCHRONOUS ZONE. DO NOT PUT ANYTHING SLOW HERE.
			//
			// Seriously. This is the inner editor loop. We expect this code to run in about 8µs on
			// a fast MacBook Pro. Do not put anything in here that isn't absolutely necessary.
			const container = get($editorStateValue(key));
			if (!container || !container.state) {
				logger.error("Cannot apply transaction: editor state not initialized", { key });
				const error = new PmEditorStateNotLoadedError();
				// Set error state for tracking, but throw so the caller knows
				set($editorStateValue(key), {
					...container,
					state: null,
					params: { key },
					error,
					docChanged: transaction.docChanged,
				});
				throw error;
			}
			const { state: current, params, error } = container;

			if (params.isReadOnly) {
				// If read-only, reject transactions that make changes to the document contents
				if (transaction.docChanged) {
					logger.warn("Transaction rejected: tried to change a read-only editor");
					return;
				}

				// but allow transactions that just move the cursor or change selection
				const newState = current.apply(transaction);
				set($editorStateValue(key), { ...container, state: newState });

				// Return early to skip persistence stuff
				return;
			}

			// Check if editor is halted.
			if (error) {
				throw new PmTransactionProcHaltedError(error);
			}

			// Check if editor is misconfigured (not writing to collab or disk)
			if (!params.sendToCollab && !params.onPersist) {
				throw new PmMisconfiguredError("Transaction failed: persistence not configured");
			}

			// DEBUG: Log transaction state mismatch before it causes an error
			// The transaction's "before" state should match the current editor state
			const transactionBefore = transaction.before;
			if (transactionBefore !== current.doc) {
				logger.error("Transaction state mismatch detected", {
					editorStateKey: key,
					data: safeStringify({
						currentDocFirstChild: current.doc.firstChild?.type.name,
						currentDocSize: current.doc.content.size,
						transactionBeforeFirstChild: transactionBefore.firstChild?.type.name,
						transactionBeforeSize: transactionBefore.content.size,
						transactionStepsCount: transaction.steps.length,
						transactionDocChanged: transaction.docChanged,
					}),
				});
			}

			// Update. NEVER change params or error.
			let newState: EditorState;
			try {
				newState = current.apply(transaction);
			} catch (err) {
				logger.error("Failed to apply transaction", {
					editorStateKey: key,
					error: err instanceof Error ? err.message : String(err),
					currentDocFirstChild: current.doc.firstChild?.type.name,
					currentDocSize: current.doc.content.size,
					transactionBeforeFirstChild: transactionBefore.firstChild?.type.name,
					transactionBeforeSize: transactionBefore.content.size,
					transactionStepsCount: transaction.steps.length,
					transactionDocChanged: transaction.docChanged,
				});
				throw err;
			}

			// Accumulate steps for 3-way merge (used when applying external file changes).
			// Only accumulate if we have disk persistence, the transaction changed the doc,
			// and this is NOT an external change (we only track user's local edits).
			let foreignSteps = container.foreignSteps;
			const isExternalChange = transaction.getMeta("externalChange") === true;
			if (params.onPersist && transaction.docChanged && !isExternalChange) {
				foreignSteps = [...(foreignSteps ?? []), ...transaction.steps];
			}

			set($editorStateValue(key), {
				...container,
				state: newState,
				foreignSteps,
				docChanged: transaction.docChanged,
			});
			// IMPORTANT: END SYNCHRONOUS ZONE. ////////////////////////////////////////////////////
			////////////////////////////////////////////////////////////////////////////////////////
		}
	);

	$stateAtom.debugLabel = `editorState::${key}`;
	return $stateAtom;
});

/**
 * Tracks the latest persist version for each editor key. Used to ensure only the latest
 * persist callback clears foreignSteps (avoiding race conditions when multiple persists
 * are in flight). Stored outside atom state to avoid triggering re-renders.
 *
 * IMPORTANT: Do NOT delete entries from this Map on editor unload. If we did, and then
 * reloaded an editor with the same key while an old persist was still in flight, the old
 * persist's .then() could pass the version check and corrupt the new editor's state.
 * The "leak" (one number per key ever used) is acceptable and bounded.
 */
const persistVersions = new Map<string, number>();
const editorStateEffectMountCounts = new Map<string, number>();

function incrementEditorStateEffectMountCount(key: string): void {
	editorStateEffectMountCounts.set(key, (editorStateEffectMountCounts.get(key) ?? 0) + 1);
}

function decrementEditorStateEffectMountCount(key: string): void {
	const currentCount = editorStateEffectMountCounts.get(key) ?? 0;
	if (currentCount <= 1) {
		editorStateEffectMountCounts.delete(key);
		return;
	}

	editorStateEffectMountCounts.set(key, currentCount - 1);
}

function runEditorStateSideEffects(key: string, get: Getter, set: Setter): void {
	////////////////////////////////////////////////////////////////////////////////////////////
	// IMPORTANT: EXTREME CRITICAL ZONE. This is the CORE PERSISTENCE AND COLLAB LOOP. Changing
	// this code could result in DATA LOSS, STATE TEARING, and OTHER ERRATIC BEHAVIOR.
	//
	//   - DO NOT AWAIT ANYTHING.
	//   - DO NOT CHANGE THE ORDER OF ANY OF THESE STATEMENTS UNLESS YOU THINK CAREFULLY.
	//   - DO NOT ADD THINGS TO IT IF YOU DO NOT KNOW WHAT YOU ARE DOING.
	//
	// The LEGITIMATELY TRICKY part of this loop is the `sendableCommit` call below, which is
	// (via a secret side-effect) the ONLY REASON COLLAB WORKS AT ALL. See inline comment for
	// more details.
	const container = get($editorStateValue(key));
	if (!container?.state || container.error) {
		return;
	}
	const { state, params } = container;

	const collabState = collabKey.getState(state);
	const unconfirmedSteps = collabState && collabState.unconfirmed.length > 0;

	if (params.sendToCollab && unconfirmedSteps) {
		// !!! EXTREMELY SENSITIVE CODE. THIS IS THE REASON COLLAB WORKS AT ALL.
		// `sendableCommit` secretly mutates the `state` argument in-place, and it is CRITICALLY
		// IMPORTANT that it happens here, in this order, with the accompanying `set`.
		//
		// Some additional details:
		//
		//   - `sendableCommit(state)` here is actually, secretly, setting the `commit` field of
		//     the `collabKey.getState(state)` object, if one does not exist.
		//   - This `commit` field has a `ref` (essentially a unique ID).
		//   - We need this `ref` so that when the server confirms the commit, we can know which
		//     commit to mark as acknowledged.
		//   - After calling `sendableCommit`, you MUST call `set`, IMMEDIATELY AFTER. No
		//     suspense points, no waiting. Call `set` immediately.
		//   - The point of this is to avoid races: if transactions happen between calling
		//     `sendableCommit` and `set`, then the call to `sendableCommit` has no effect.
		//   - This is because `sendableCommit` mutates the `state` argument in-place, NOT the
		//     `EditorState` objects for later transactions.
		sendableCommit(state);
		set($editorStateValue(key), { ...container, state });
		// !!!!!!!!!!!!!!!!!

		void params.sendToCollab(state).catch((err) => {
			logger.error("Failed to send to collab", { key, err });
		});
	}
	// IMPORTANT: END EXTREME CRITICAL ZONE. ///////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////

	// Presence - always update (includes cursor movements)
	if (params.updatePresence) {
		void params.updatePresence(state).catch((err) => {
			logger.error("Failed to update presence", { key, err });
		});
	}

	// Persist to disk.
	const localOnly = !collabState;
	if (params.onPersist && (localOnly || unconfirmedSteps) && container.docChanged) {
		// Capture how many steps exist now, so we only remove those after persist completes.
		// Any steps added during async persist will be retained.
		const stepsAtPersistStart = container.foreignSteps?.length ?? 0;

		// Increment persist version to track this persist. Only the latest persist (matching
		// version) should clear foreignSteps when it completes. We store this in a Map outside
		// atom state to avoid triggering effect re-runs.
		const currentVersion = persistVersions.get(key) ?? 0;
		const nextVersion = currentVersion + 1;
		persistVersions.set(key, nextVersion);

		void params
			.onPersist(get, set, state)
			.then(() => {
				// On successful persist, update lastPersistedDoc and remove only the steps
				// that existed when persist started. Keep any steps added during async persist.
				// Only do this if we're still the latest persist (no newer persist has started).
				const currentContainer = get($editorStateValue(key));
				if (!currentContainer) {
					return;
				}

				// If a newer persist has started, skip clearing steps - let that persist handle
				// it.
				if (persistVersions.get(key) !== nextVersion) {
					return;
				}

				const currentSteps = currentContainer.foreignSteps ?? [];
				set($editorStateValue(key), {
					...currentContainer,
					lastPersistedDoc: state.doc,
					foreignSteps: currentSteps.slice(stepsAtPersistStart),
				});
			})
			.catch((err) => {
				logger.error("Failed to persist", { key, err });

				// Set critical error, halt editor.
				const currentContainer = get($editorStateValue(key));
				if (!currentContainer) {
					return;
				}

				const error = new PmPersistError(err);
				set($editorStateValue(key), { ...currentContainer, error });
			});
	}
}

/**
 * Handles side effects for editor state changes: collab sync, presence updates, and persistence.
 * Must be mounted (via useAtomValue) for the effects to run.
 */
export const $editorStateEffect = atomFamily((key: string) =>
	atomEffect((get, set) => {
		incrementEditorStateEffectMountCount(key);
		runEditorStateSideEffects(key, get, set);

		return () => {
			decrementEditorStateEffectMountCount(key);
		};
	})
);

/**
 * Runs the editor-state side effects for a key without requiring a mounted
 * `$editorStateEffect` subscription.
 *
 * This exists for batch jobs that lazy-load editor states and apply a single
 * transaction offscreen (for example page-link rewrites during renames). Those
 * states have no mounted React hook, so the normal effect never runs.
 *
 * Keep usage extremely narrow. If a caller can rely on a mounted
 * `$editorStateEffect`, it should do that instead.
 */
export const $flushEditorStateSideEffects = atom(null, (get, set, key: string) => {
	if ((editorStateEffectMountCounts.get(key) ?? 0) > 0) {
		return;
	}

	runEditorStateSideEffects(key, get, set);
});
$flushEditorStateSideEffects.debugLabel = "flushEditorStateSideEffects";

/**
 * Starts the collab listeners (remote changes and presence) for a given editor state key.
 * Called from $loadEditorState after the container is set.
 */
function startCollabListeners(
	key: string,
	abortController: AbortController,
	get: Getter,
	set: Setter
): void {
	const container = get($editorStateValue(key));
	if (!container) {
		logger.error("Cannot start collab listeners: container not initialized", { key });
		return;
	}
	const { remoteChanges, receivePresence, localClientId } = container.params;

	//
	// SUBSCRIBE TO REMOTE CHANGES TO THE DOCUMENT.
	//

	void (async function listenForRemoteChanges() {
		if (!remoteChanges) {
			return;
		}

		try {
			logger.info("Starting remote changes listener", { key });

			const iterator = remoteChanges({ signal: abortController.signal });
			for await (const commits of iterator) {
				if (abortController.signal.aborted) {
					break;
				}

				// Get current container
				const currentContainer = get($editorStateValue(key));
				if (!currentContainer || !currentContainer.state) {
					logger.warn("Cannot apply remote commits: state not initialized", { key });
					continue;
				}
				const { state, abortController: ac } = currentContainer;

				// Apply all commits
				const newState = commits.reduce((state: EditorState, commit: Commit) => {
					const tr = receiveCommitTransaction(state, commit);
					try {
						return state.apply(tr);
					} catch (err) {
						logger.error("Failed to apply remote commit transaction", {
							editorStateKey: key,
							error: err instanceof Error ? err.message : String(err),
							commitRef: commit.ref,
							stateDocSize: state.doc.content.size,
							transactionBeforeSize: tr.before.content.size,
							transactionStepsCount: tr.steps.length,
						});
						throw err;
					}
				}, state);

				// Update state while preserving params, error, and abortController.
				set($editorStateValue(key), {
					...currentContainer,
					state: newState,
					abortController: ac,
					// Always true for remote commits.
					docChanged: true,
				});

				// Update presence step maps and clean stale indicators.
				set($onRemoteCommitsReceived, {
					key,
					newVersion: getVersion(newState) ?? 0,
					commits,
				});
			}
		} catch (err) {
			if (abortController.signal.aborted) {
				return;
			}

			logger.error("Remote changes listener error", { key, err });

			// Set critical error.
			const errorContainer = get($editorStateValue(key));
			if (!errorContainer) {
				return;
			}
			const error = new PmCollabSubscriptionFailureError(err);
			set($editorStateValue(key), {
				...errorContainer,
				error,
			});
		}
	})();

	//
	// SUBSCRIBE TO PRESENCE UPDATES.
	//

	void (async function listenForPresenceChanges() {
		if (!receivePresence || !localClientId) {
			return;
		}

		try {
			logger.info("Starting presence listener", { key });

			const iterator = receivePresence({ signal: abortController.signal });
			for await (const indicators of iterator) {
				if (abortController.signal.aborted) {
					break;
				}

				set($onPresenceIndicatorsReceived, { key, localClientId, indicators });
			}
		} catch (err) {
			if (!abortController.signal.aborted) {
				logger.error("Presence listener error", { key, err });
			}
		}

		logger.info("Presence listener stopped", { key });
	})();
}

/**
 * Provides read-only access to the editor state error (for UI consumption).
 */
export const $editorStateError = atomFamily((key: string) => {
	const errorAtom = atom((get) => get($editorStateValue(key))?.error ?? null);
	errorAtom.debugLabel = `editorStateError::${key}`;
	return errorAtom;
});

/**
 * Provides read-only access to the collab connection (for presence hooks).
 * Only available for Reboot-loaded editor states.
 */
export const $editorStateConnection = atomFamily((key: string) => {
	const connectionAtom = atom((get) => get($editorStateValue(key))?.connection ?? null);
	connectionAtom.debugLabel = `editorStateConnection::${key}`;
	return connectionAtom;
});

/**
 * Provides read-only access to the 3-way merge state for external file changes.
 * Returns the lastPersistedDoc and foreignSteps needed to compute a mapping.
 */
export const $editorStateMergeInfo = atomFamily((key: string) => {
	const mergeInfoAtom = atom((get) => {
		const container = get($editorStateValue(key));
		if (!container) {
			return null;
		}
		return {
			lastPersistedDoc: container.lastPersistedDoc,
			foreignSteps: container.foreignSteps,
		};
	});
	mergeInfoAtom.debugLabel = `editorStateMergeInfo::${key}`;
	return mergeInfoAtom;
});

/**
 * Updates the 3-way merge state after applying an external change.
 * Sets the new lastPersistedDoc and clears foreignSteps.
 */
export const $updateMergeInfo = atom(
	null,
	(get, set, input: { key: string; lastPersistedDoc: Node }) => {
		const container = get($editorStateValue(input.key));
		if (!container) {
			return;
		}
		set($editorStateValue(input.key), {
			...container,
			lastPersistedDoc: input.lastPersistedDoc,
			foreignSteps: [],
		});
	}
);
$updateMergeInfo.debugLabel = "updateMergeInfo";

export type LoadEditorStateInput = EditorStateParams &
	CreateEditorStateInput & {
		/** Optional collab connection, only set for Reboot-loaded states */
		connection?: MdPageCollabConnection;
	};

/**
 * Loads an editor state from `doc` JSON and some set of `plugins`. Once called and succeeds, the
 * editor state at `key` will be full initialized and (if configured to be writable) ready to accept
 * transactions.
 */
export const $loadEditorState = atom(null, (get, set, input: LoadEditorStateInput) => {
	const {
		key,
		doc,
		plugins,
		ssr = false,
		isReadOnly,
		remoteChanges,
		sendToCollab,
		onPersist,
		receivePresence,
		localClientId,
		updatePresence,
		connection,
	} = input;

	// Check if editor is misconfigured (not in SSR mode, not read-only, and not writing to collab or disk)
	if (!ssr && !isReadOnly && !sendToCollab && !onPersist) {
		throw new PmMisconfiguredError("Failed to load editor state: persistence not configured");
	}

	const state = createEditorState({ doc, plugins, ssr });

	// Create AbortController for managing collab listeners lifecycle
	const abortController = new AbortController();

	const params: EditorStateParams = {
		key,
		isReadOnly,
		remoteChanges,
		sendToCollab,
		onPersist,
		receivePresence,
		localClientId,
		updatePresence,
	};

	// Initialize lastPersistedDoc for disk persistence (enables 3-way merge for external changes).
	const lastPersistedDoc = onPersist ? state.doc : undefined;
	const foreignSteps = onPersist ? [] : undefined;

	set($editorStateValue(key), {
		state,
		params,
		error: null,
		connection,
		abortController,
		lastPersistedDoc,
		foreignSteps,
		docChanged: false,
	});

	// Start collab listeners after setting the container (ensures no race condition)
	if (!ssr) {
		startCollabListeners(key, abortController, get, set);
	}
});
$loadEditorState.debugLabel = "loadEditorState";

export type SetEditorStateErrorInput = {
	key: string;
	error: EditorStateContainer["error"];
};

/**
 * Sets an error on the editor state at `key`. Used mostly by loaders when loading fails.
 */
export const $setEditorStateError = atom(null, (get, set, input: SetEditorStateErrorInput) => {
	const { key, error } = input;
	const container = get($editorStateValue(key));
	const state = container?.state ?? null;
	set($editorStateValue(key), {
		...(container ?? {}),
		state,
		params: { key },
		error,
		docChanged: false,
	});
});
$setEditorStateError.debugLabel = "setEditorStateError";

/**
 * Unloads an editor state from `key`, freeing all resources associated with it. Once successfully
 * completed, all subsequent reads and writes to the editor state at `key` will throw an error.
 */
export const $unloadEditorState = atom(null, (get, set, key: string) => {
	// Abort collab listeners before cleaning up
	const container = get($editorStateValue(key));
	if (container?.abortController) {
		logger.info("Stopping collab listeners", { key });
		container.abortController.abort();
	}

	set($editorStateValue(key), null);
	$editorStateValue.remove(key);
	$editorState.remove(key);

	// Clean up presence state for this key
	set($cleanupPresence, key);
});

/**
 * Provides read-only access to the editor state params (for testing).
 */
export const $editorStateConfig = atomFamily((key: string) => {
	const configAtom = atom((get) => get($editorStateValue(key))?.params ?? null);
	configAtom.debugLabel = `editorStateConfig::${key}`;
	return configAtom;
});
